import React, { useEffect, useState } from "react";
import classes from "./DashboardSummary.module.css";
import { env } from "../../../constants/env";

const DashboardSummary = ({ accountNumber }) => {
  const [trackingData, setTrackingData] = useState({
    balance: 0,
    equity: 0,
    dailyStopOut: 0,
    trailingLossStopOut: 0,
    profitCapOffence: false,
    totalWithdrawals: 0,
  });

  const [monthlyGain, setMonthlyGain] = useState({});

  useEffect(() => {
    if (!accountNumber) return;

    fetchTracking();
    fetchMonthlyGain();
  }, [accountNumber]);

  const fetchTracking = async () => {
    try {
      const res = await fetch(
        `${env.primeBridgeUrl}/api/account/account-statistics/${accountNumber}`
      );
      if (!res.ok) throw new Error("Failed to fetch account tracking");
      const data = await res.json();

      setTrackingData({
        balance: data.balance || 0,
        equity: data.equity || 0,
        dailyStopOut: data.dailyStopOut || 0,
        trailingLossStopOut: data.trailingLossStopOut || 0,
        profitCapOffence: data.profitCapOffence || false,
        totalWithdrawals: data.totalWithdrawals || 0,
      });
    } catch (err) {
      console.error("Error fetching account tracking:", err);
    }
  };

  const fetchMonthlyGain = async () => {
    try {
      const res = await fetch(
        `${env.primeBridgeUrl}/api/account/monthly-gain/${accountNumber}`
      );
      if (!res.ok) throw new Error("Failed to fetch monthly gain");
      const data = await res.json();
      setMonthlyGain(data);
    } catch (err) {
      console.error(err);
    }
  };

  const formatCurrency = (value) =>
    `$${Number(value).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  return (
    <div className={classes.container}>
      <div className={classes.notice}>
        This portal updates every 5 minutes; data may differ from MT5 until the
        next update.
      </div>

      <div className={classes.summarySection}>
        <div className={classes.summaryBox}>
          <h5>Summary</h5>

          <div className={classes.summaryColumns}>
            <div className={classes.leftColumn}>
              <div className="d-flex align-items-center justify-content-between">
                <p>Current Balance</p>
                <span className={classes.value}>
                  {formatCurrency(trackingData.balance)}
                </span>
              </div>

              <div className="d-flex align-items-center justify-content-between">
                <p>Current Equity</p>
                <span className={classes.value}>
                  {formatCurrency(trackingData.equity)}
                </span>
              </div>

              <div className="d-flex align-items-center justify-content-between">
                <p className={classes.infoWrapper}>
                  Daily Loss Stop Out{" "}
                  <span className={classes.info}>
                    ⓘ
                    <span className={classes.tooltip}>
                      This is the equity level at which your account would have
                      breached the max daily loss rule
                    </span>
                  </span>
                </p>
                <span className={classes.value}>
                  {formatCurrency(trackingData.dailyStopOut)}
                </span>
              </div>

              <div className="d-flex align-items-center justify-content-between">
                <p>
                  Trailing Loss Stop Out{" "}
                  <span className={classes.info}>
                    ⓘ
                    <span className={classes.tooltip}>
                      This is the equity level at which your account would have
                      breached the trailing drawdown rule
                    </span>
                  </span>
                </p>
                <span className={classes.value}>
                  {formatCurrency(trackingData.trailingLossStopOut)}
                </span>
              </div>
            </div>

            <div className={classes.divider}></div>

            <div className={classes.rightColumn}>
              <div className="d-flex align-items-center justify-content-between">
                <p>Profit cap offence</p>
                <span className={classes.value}>
                  {trackingData.profitCapOffence ? trackingData.profitCapOffence : "No"}
                </span>
              </div>

              <div className="d-flex align-items-center justify-content-between">
                <p>Total Withdrawals</p>
                <span className={classes.value}>
                  {formatCurrency(trackingData.totalWithdrawals)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={classes.monthlySection}>
        <h5>Monthly Gain</h5>
        <div className={classes.table}>
          <div className={classes.headerRow}>
            <span>Year</span>
            {[
              "Jan",
              "Feb",
              "Mar",
              "Apr",
              "May",
              "Jun",
              "Jul",
              "Aug",
              "Sept",
              "Oct",
              "Nov",
              "Dec",
              "Total",
            ].map((month) => (
              <span key={month}>{month}</span>
            ))}
          </div>

          {Object.keys(monthlyGain).length === 0 ? (
            <div className={classes.noData}>No monthly gain data available</div>
          ) : (
            Object.entries(monthlyGain).map(([year, months]) => {
              // Calculate total (only numeric values)
              const total = Object.values(months)
                .filter((v) => !isNaN(parseFloat(v)))
                .reduce((sum, val) => sum + parseFloat(val), 0)
                .toFixed(2);

              return (
                <div key={year} className={classes.dataRow}>
                  <span>{year}</span>
                  {[
                    "Jan",
                    "Feb",
                    "Mar",
                    "Apr",
                    "May",
                    "Jun",
                    "Jul",
                    "Aug",
                    "Sept",
                    "Oct",
                    "Nov",
                    "Dec",
                  ].map((month) => (
                    <span key={month}>
                      {months[month] && !isNaN(months[month])
                        ? `${months[month]}%`
                        : months[month] || "-"}
                    </span>
                  ))}
                  <span>{total}%</span>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardSummary;
